import axios from "axios";
import {
  fetchEducationDetailsByParent,
  syncEducationEntries,
} from "./educationService";
import { fetchWorkDetailsByParent, syncWorkEntries } from "./workService";
import { fetchIDCardDetailsByParent, syncIDCardEntries } from "./idCardService";
import { fetchBankDetailsByParent, syncBankDetailsEntries } from "./bankDetailsService";
import { fetchSocialLinksByParent, syncSocialLinksEntries } from "./socialLinksService";

// Helper function to call SuiteCRM API
const callSuiteCrmApi = async (method, data) => {
  const apiUrl = "https://seltentechnologiesdemo.com/moona-backend/service/v4_1/rest.php";
  const params = new URLSearchParams();
  params.append("method", method);
  params.append("input_type", "JSON");
  params.append("response_type", "JSON");
  params.append("rest_data", JSON.stringify(data));

  const response = await axios.post(apiUrl, params, {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });

  return response.data;
};

// Build education entries payload
const buildEducationEntriesPayload = (formData, user_id) => {
  const maxSections = 5;
  const entries = [];

  for (let i = 1; i <= maxSections; i++) {
    const qualificationRaw = formData[`qualification${i}`] ?? formData[`education${i}`] ?? "";
    const qualification =
      typeof qualificationRaw === "string" ? qualificationRaw.trim() : qualificationRaw;

    const instituteValue = formData[`instituteschool${i}`] ?? formData[`institute${i}`] ?? "";
    const institute =
      typeof instituteValue === "string" ? instituteValue.trim() : instituteValue;

    const educationTypeRaw = formData[`educationtype${i}`] ?? "";
    const educationtype =
      typeof educationTypeRaw === "string" ? educationTypeRaw.trim() : educationTypeRaw;

    const boardRaw = formData[`board${i}`] ?? "";
    const board = typeof boardRaw === "string" ? boardRaw.trim() : boardRaw;

    const courseRaw = formData[`course${i}`] ?? "";
    const course = typeof courseRaw === "string" ? courseRaw.trim() : courseRaw;

    const degreetypeRaw = formData[`degreetype${i}`] ?? "";
    const degreetype = typeof degreetypeRaw === "string" ? degreetypeRaw.trim() : degreetypeRaw;

    const startyear_c = formData[`startyear_c${i}`] || "";
    const endyear_c = formData[`endyear_c${i}`] || "";
    const currentlystudying_c = formData[`currentlystudying_c${i}`] || false;

    const gradeRaw = formData[`grade${i}`] ?? "";
    const grade = typeof gradeRaw === "string" ? gradeRaw.trim() : gradeRaw;

    const cityRaw = formData[`instituecity${i}`] ?? "";
    const instituecity = typeof cityRaw === "string" ? cityRaw.trim() : cityRaw;

    const stateValue = formData[`institutestate${i}`];
    const institutestate =
      typeof stateValue === "string"
        ? stateValue.trim()
        : stateValue?.name || stateValue?.value || "";

    const countryValue = formData[`institutecountry${i}`];
    const institutecountry =
      typeof countryValue === "string"
        ? countryValue.trim()
        : countryValue?.name || countryValue?.value || "";

    const pincodeRaw = formData[`institutepincode${i}`] ?? "";
    const institutepincode = typeof pincodeRaw === "string" ? pincodeRaw.trim() : pincodeRaw;

    const institutetypeRaw = formData[`institutetype${i}`] ?? "";
    const institutetype =
      typeof institutetypeRaw === "string" ? institutetypeRaw.trim() : institutetypeRaw;

    const descriptionRaw =
      formData[`description${i}`] ?? formData[`educationdescription${i}`] ?? "";
    const description =
      typeof descriptionRaw === "string" ? descriptionRaw.trim() : descriptionRaw;

    const entryId = formData[`educationEntryId${i}`] || "";
    const entryNameSource =
      formData[`educationname${i}`] ??
      formData[`educationName${i}`] ??
      qualification ??
      institute;
    const entryName =
      typeof entryNameSource === "string" && entryNameSource.trim().length > 0
        ? entryNameSource.trim()
        : `Education ${i}`;

    // Only create entry if at least the core required fields are present
    const hasCoreRequiredFields =
      qualification &&
      typeof qualification === "string" &&
      qualification.trim().length > 0 &&
      board &&
      typeof board === "string" &&
      board.trim().length > 0 &&
      educationtype &&
      typeof educationtype === "string" &&
      educationtype.trim().length > 0 &&
      institute &&
      typeof institute === "string" &&
      institute.trim().length > 0;

    if (!hasCoreRequiredFields) {
      continue;
    }

    entries.push({
      id: entryId || undefined,
      name: entryName,
      parentid: user_id,
      qualification: typeof qualification === "string" ? qualification : qualification ?? "",
      educationtype,
      institute: typeof institute === "string" ? institute : institute ?? "",
      instituecity,
      institutestate,
      institutecountry,
      institutepincode,
      board,
      course,
      degreetype,
      startyear_c,
      endyear_c,
      currentlystudying_c,
      grade,
      description,
      institutetype,
      section: i,
    });
  }

  return entries;
};

// Build work entries payload
const buildWorkEntriesPayload = (formData, user_id) => {
  const maxSections = 5;
  const entries = [];

  for (let i = 1; i <= maxSections; i++) {
    const companynameRaw = formData[`companyname${i}`] ?? "";
    const companyname =
      typeof companynameRaw === "string" ? companynameRaw.trim() : companynameRaw;

    const designationRaw = formData[`designation${i}`] ?? "";
    const designation =
      typeof designationRaw === "string" ? designationRaw.trim() : designationRaw;

    const departmentRaw = formData[`department${i}`] ?? "";
    const department = typeof departmentRaw === "string" ? departmentRaw.trim() : departmentRaw;

    const employmenttypeRaw = formData[`employmenttype${i}`] ?? "";
    const employmenttype =
      typeof employmenttypeRaw === "string" ? employmenttypeRaw.trim() : employmenttypeRaw;

    const sectorRaw = formData[`sector${i}`] ?? "";
    const sector = typeof sectorRaw === "string" ? sectorRaw.trim() : sectorRaw;

    const startdate = formData[`startdate${i}`] || "";
    const enddate = formData[`enddate${i}`] || "";
    // Convert currentlyworking to proper boolean (0 or 1 for database)
    const currentlyworkingRaw = formData[`currentlyworking${i}`];
    const currentlyworking =
      currentlyworkingRaw === true || currentlyworkingRaw === "1" || currentlyworkingRaw === 1
        ? 1
        : 0;

    const descriptionRaw = formData[`description${i}`] ?? "";
    const description =
      typeof descriptionRaw === "string" ? descriptionRaw.trim() : descriptionRaw;

    const addressRaw = formData[`address${i}`] ?? "";
    const address = typeof addressRaw === "string" ? addressRaw.trim() : addressRaw;

    const countryValue = formData[`country${i}`];
    const country =
      typeof countryValue === "string"
        ? countryValue.trim()
        : countryValue?.name || countryValue?.value || "";

    const stateValue = formData[`state${i}`];
    const state =
      typeof stateValue === "string"
        ? stateValue.trim()
        : stateValue?.name || stateValue?.value || "";

    const cityRaw = formData[`city${i}`] ?? "";
    const city = typeof cityRaw === "string" ? cityRaw.trim() : cityRaw;

    const pincodeRaw = formData[`pincode${i}`] ?? "";
    const pincode = typeof pincodeRaw === "string" ? pincodeRaw.trim() : pincodeRaw;

  const imageValue = formData[`workimage${i}`];

    const entryId = formData[`workEntryId${i}`] || undefined;
    const entryName = companyname || `Work Experience ${i}`;

    // Only create entry if at least the core required fields are present
    const hasCoreRequiredFields =
      companyname &&
      typeof companyname === "string" &&
      companyname.trim().length > 0 &&
      designation &&
      typeof designation === "string" &&
      designation.trim().length > 0 &&
      employmenttype &&
      typeof employmenttype === "string" &&
      employmenttype.trim().length > 0 &&
      startdate &&
      typeof startdate === "string" &&
      startdate.trim().length > 0;

    if (!hasCoreRequiredFields) {
      continue;
    }

    const workEntry = {
      id: entryId || undefined,
      name: entryName,
      parentid: user_id,
      companyname: typeof companyname === "string" ? companyname : companyname ?? "",
      designation,
      department,
      employmenttype,
      sector,
      startdate,
      currentlyworking,
      description,
      address,
      country,
      state,
      city,
      pincode,
      section: i,
    };

    if (imageValue !== undefined) {
      workEntry.image = imageValue;
    }

    // Handle enddate based on currently working status
    if (currentlyworking) {
      // If currently working, explicitly clear the end date
      workEntry.enddate = "";
    } else {
      // If not currently working, include the end date (even if empty)
      workEntry.enddate = enddate || "";
    }

    entries.push(workEntry);
  }

  return entries;
};

// Build ID card entries payload
const buildIDCardEntriesPayload = (formData, user_id) => {
  const maxSections = 10;
  const entries = [];

  for (let i = 1; i <= maxSections; i++) {
    const idtypeRaw = formData[`idtype${i}`] ?? "";
    const idtype = typeof idtypeRaw === "string" ? idtypeRaw.trim() : idtypeRaw;

    const idcardnameRaw = formData[`idcardname${i}`] ?? "";
    const idcardname = typeof idcardnameRaw === "string" ? idcardnameRaw.trim() : idcardnameRaw;

    const idnumberRaw = formData[`idnumber${i}`] ?? "";
    const idnumber = typeof idnumberRaw === "string" ? idnumberRaw.trim() : idnumberRaw;

    const issuedateRaw = formData[`issuedate${i}`] ?? "";
    const issuedate = typeof issuedateRaw === "string" ? issuedateRaw.trim() : issuedateRaw;

    const expirydateRaw = formData[`expirydate${i}`] ?? "";
    const expirydate = typeof expirydateRaw === "string" ? expirydateRaw.trim() : expirydateRaw;

    const idcarddescriptionRaw = formData[`idcarddescription${i}`] ?? "";
    const idcarddescription =
      typeof idcarddescriptionRaw === "string"
        ? idcarddescriptionRaw.trim()
        : idcarddescriptionRaw;

    const entryId = formData[`idCardEntryId${i}`] || undefined;

    // Only create entry if at least the three core required fields are present
    const hasCoreRequiredFields =
      idtype &&
      typeof idtype === "string" &&
      idtype.trim().length > 0 &&
      idcardname &&
      typeof idcardname === "string" &&
      idcardname.trim().length > 0 &&
      idnumber &&
      typeof idnumber === "string" &&
      idnumber.trim().length > 0;

    if (!hasCoreRequiredFields) {
      continue;
    }

    const idCardEntry = {
      id: entryId || undefined,
      name: idcardname || `ID Card ${i}`,
      parentid: user_id,
      idtype,
      idcardname,
      idnumber, // Will be encrypted by service
      issuedate, // Will be encrypted by service
      expirydate, // Will be encrypted by service
      idcarddescription,
      section: i,
    };

    entries.push(idCardEntry);
  }

  return entries;
};

// Build bank entries payload
const buildBankEntriesPayload = (formData, user_id) => {
  const maxSections = 10;
  const entries = [];

  for (let i = 1; i <= maxSections; i++) {
    const accountholdernameRaw = formData[`accountholdername${i}`] ?? "";
    const accountholdername = typeof accountholdernameRaw === "string" ? accountholdernameRaw.trim() : accountholdernameRaw;

    const accountnumberRaw = formData[`accountnumber${i}`] ?? "";
    const accountnumber = typeof accountnumberRaw === "string" ? accountnumberRaw.trim() : accountnumberRaw;

    const ifscRaw = formData[`ifsc${i}`] ?? "";
    const ifsc = typeof ifscRaw === "string" ? ifscRaw.trim() : ifscRaw;

    const accounttypeRaw = formData[`accounttype${i}`] ?? "";
    const accounttype = typeof accounttypeRaw === "string" ? accounttypeRaw.trim() : accounttypeRaw;

    const banknameRaw = formData[`bankname${i}`] ?? "";
    const bankname = typeof banknameRaw === "string" ? banknameRaw.trim() : banknameRaw;

    const branchRaw = formData[`branch${i}`] ?? "";
    const branch = typeof branchRaw === "string" ? branchRaw.trim() : branchRaw;

    const bankcountryRaw = formData[`bankcountry${i}`] ?? "";
    const bankcountry = typeof bankcountryRaw === "string" ? bankcountryRaw.trim() : bankcountryRaw;

    const statusRaw = formData[`status${i}`] ?? "Active";
    const status = typeof statusRaw === "string" ? statusRaw.trim() : statusRaw;

    const bankdescriptionRaw = formData[`bankdescription${i}`] ?? "";
    const bankdescription = typeof bankdescriptionRaw === "string" ? bankdescriptionRaw.trim() : bankdescriptionRaw;

  const imageRaw = formData[`image${i}`] ?? "";
  const image = typeof imageRaw === "string" ? imageRaw : "";

    const entryId = formData[`bankEntryId${i}`] || undefined;

    // Only create entry if all required fields are present
    const hasCoreRequiredFields =
      accountholdername &&
      typeof accountholdername === "string" &&
      accountholdername.trim().length > 0 &&
      accountnumber &&
      typeof accountnumber === "string" &&
      accountnumber.trim().length > 0 &&
      ifsc &&
      typeof ifsc === "string" &&
      ifsc.trim().length > 0 &&
      bankname &&
      typeof bankname === "string" &&
      bankname.trim().length > 0 &&
      branch &&
      typeof branch === "string" &&
      branch.trim().length > 0;

    if (!hasCoreRequiredFields) {
      continue;
    }

    const bankEntry = {
      id: entryId || undefined,
      name: `${bankname} - ${accountholdername}`,
      parentid_c: user_id,
      accountholdername,
      accountnumber, // Will be encrypted by service
      ifsc, // Will be encrypted by service
      accounttype,
      bankname,
      branch,
      bankcountry,
      status,
      bankdescription,
      image,
      section: i,
    };

    entries.push(bankEntry);
  }

  return entries;
};

// Build social links entries payload
const buildSocialLinksEntriesPayload = (formData, user_id) => {
  const maxSections = 10;
  const entries = [];

  for (let i = 1; i <= maxSections; i++) {
    const typeRaw = formData[`socialtype${i}`] ?? "";
    const type = typeof typeRaw === "string" ? typeRaw.trim() : typeRaw;

    const linkRaw = formData[`sociallink${i}`] ?? "";
    const link = typeof linkRaw === "string" ? linkRaw.trim() : linkRaw;

    const entryId = formData[`socialLinksEntryId${i}`] || undefined;

    // Only create entry if both type and link are present
    const hasCoreRequiredFields =
      type &&
      typeof type === "string" &&
      type.trim().length > 0 &&
      link &&
      typeof link === "string" &&
      link.trim().length > 0;

    if (!hasCoreRequiredFields) {
      continue;
    }

    const socialLinkEntry = {
      id: entryId || undefined,
      name: `${type} Link`,
      parentid_c: user_id,
      type,
      link,
      section: i,
    };

    entries.push(socialLinkEntry);
  }

  return entries;
};

/**
 * Submit profile data to SuiteCRM
 * @param {Object} formData - Form data from the profile edit dialog
 * @param {string} user_id - User ID
 * @param {string} sessionId - Session ID
 * @param {Function} dispatch - Redux dispatch function
 * @param {Function} setUserPhoto - Redux action to set user photo
 * @returns {Promise<Object>} Updated profile data
 */
export const handleSubmitProfile = async (formData, user_id, sessionId, dispatch, setUserPhoto) => {
  const explicitRemovals = Array.isArray(formData.removedEducationIds)
    ? formData.removedEducationIds.filter(Boolean)
    : [];

  const removalSet = new Set(explicitRemovals);

  const sectionHasData = (section) => {
    const fieldsToCheck = [
      `qualification${section}`,
      `education${section}`,
      `instituteschool${section}`,
      `institute${section}`,
      `board${section}`,
      `course${section}`,
      `degreetype${section}`,
      `startdate${section}`,
      `enddate${section}`,
      `grade${section}`,
      `instituecity${section}`,
      `institutestate${section}`,
      `institutecountry${section}`,
      `institutepincode${section}`,
      `description${section}`,
      `institutetype${section}`,
    ];

    return fieldsToCheck.some((fieldName) => {
      const value = formData[fieldName];
      if (value === null || value === undefined) return false;
      if (typeof value === "object") {
        return Object.keys(value).length > 0;
      }
      if (typeof value === "string") {
        return value.trim().length > 0;
      }
      return true;
    });
  };

  for (let i = 1; i <= 5; i++) {
    const entryId = formData[`educationEntryId${i}`];
    if (!entryId) continue;
    if (!sectionHasData(i)) {
      removalSet.add(entryId);
    }
  }

  const removalList = Array.from(removalSet);

  const isSingle = formData.maritalstatus?.includes("Single") || false;

  const apiUrl = "https://seltentechnologiesdemo.com/moona-backend/service/v4_1/rest.php";
  const method = "set_entry";
  const input_type = "JSON";
  const response_type = "JSON";

  // Prepare name_value_list
  const nameValueList = [
    { name: "id", value: user_id },
    { name: "first_name", value: formData.firstName },
    { name: "last_name", value: formData.lastName },
    { name: "dob_c", value: formData.dob },
    { name: "address_street", value: formData.street },
    { name: "address_city", value: formData.city },
    { name: "address_state", value: formData.state?.name || formData.state || "" },
    { name: "address_country", value: formData.country?.name || formData.country || "" },
    { name: "address_postalcode", value: formData.postelCode },
    { name: "selfdescription_c", value: formData.description },
    { name: "aadhaar_c", value: formData.aadhaar },
    { name: "caste_c", value: formData.caste },
    { name: "religion_c", value: formData.religion },
    { name: "address_c", value: formData.address },
    { name: "village_c", value: formData.villageTown },
    { name: "taluk_c", value: formData.taluk },
    { name: "address1_c", value: formData.address1 },
    { name: "address2_c", value: formData.address2 },
    { name: "district_c", value: formData.district },
    { name: "age_c", value: formData.age },
    { name: "gender_c", value: formData.gender },
    { name: "bloodgroup_c", value: formData.bloodgroup },
    { name: "birthplace_c", value: formData.birthplace },
    { name: "disability_c", value: formData.disability },
    { name: "disabilitytype_c", value: formData.disabilitytype },
    { name: "alivestatus_c", value: formData.alivestatus },
    { name: "mothertongue_c", value: formData.mothertongue },
    { name: "languagesknown_c", value: formData.languagesknown },
    { name: "maritalstatus_c", value: formData.maritalstatus },
    { name: "marriagedate_c", value: isSingle ? "" : formData.marriagedate },
    { name: "marriageplace_c", value: isSingle ? "" : formData.marriageplace },
    { name: "economical_status_c", value: formData.economical_status },
    { name: "deathaddress_c", value: formData.deathaddress_c },
    { name: "deatharea_c", value: formData.deatharea_c },
    { name: "deathpincode_c", value: formData.deathpincode_c },
    { name: "deathcity_c", value: formData.deathcity_c },
    { name: "deathstate_c", value: formData.deathstate_c?.name || formData.deathstate_c || "" },
    {
      name: "deathcountry_c",
      value: formData.deathcountry_c?.name || formData.deathcountry_c || "",
    },
    { name: "deathplace_c", value: formData.deathplace_c },
    { name: "deathreason_c", value: formData.deathreason_c },
    { name: "dateofdeath_c", value: formData.dateofdeath_c },
  ];

  const rest_data = {
    session: sessionId,
    module_name: "Users",
    name_value_list: nameValueList,
  };

  // Use POST with URLSearchParams to handle large photo data
  const params = new URLSearchParams();
  params.append("method", method);
  params.append("input_type", input_type);
  params.append("response_type", response_type);
  params.append("rest_data", JSON.stringify(rest_data));

  const response = await axios.post(apiUrl, params, {
    headers: {
      "Content-Type": "application/x-www-form-urlencoded",
    },
  });

  // Handle photo upload/removal via Notes module
  if (formData.photoRemoved) {
    // Remove existing profile photo
    const searchData = {
      session: sessionId,
      module_name: "Notes",
      query: `notes.parent_id = '${user_id}' AND notes.name = 'Profile Photo'`,
      order_by: "",
      offset: 0,
      select_fields: ["id"],
      max_results: 20,
    };

    const existingNote = await callSuiteCrmApi("get_entry_list", searchData);

    if (existingNote?.entry_list?.length) {
      for (const note of existingNote.entry_list) {
        const noteId = note.id || note?.name_value_list?.id?.value;
        if (!noteId) continue;

        const deleteNoteData = {
          session: sessionId,
          module_name: "Notes",
          name_value_list: [
            { name: "id", value: noteId },
            { name: "deleted", value: 1 },
          ],
        };

        await callSuiteCrmApi("set_entry", deleteNoteData);
      }
    }
    dispatch(setUserPhoto(null));
  } else if (formData.photo && formData.photo.startsWith("data:")) {
    // Upload new photo via Notes attachment
    const searchData = {
      session: sessionId,
      module_name: "Notes",
      query: `notes.parent_id = '${user_id}' AND notes.name = 'Profile Photo'`,
      order_by: "",
      offset: 0,
      select_fields: ["id"],
      max_results: 1,
    };

    const existingNote = await callSuiteCrmApi("get_entry_list", searchData);
    let noteId;

    if (existingNote?.entry_list?.length) {
      noteId = existingNote.entry_list[0].id;
    } else {
      const noteData = {
        session: sessionId,
        module_name: "Notes",
        name_value_list: [
          { name: "name", value: "Profile Photo" },
          { name: "parent_type", value: "Users" },
          { name: "parent_id", value: user_id },
        ],
      };

      const noteResponse = await callSuiteCrmApi("set_entry", noteData);
      if (!noteResponse?.id) {
        throw new Error("Failed to create notes record");
      }
      noteId = noteResponse.id;
    }

    // Extract base64 data and mime type
    const matches = formData.photo.match(/^data:([A-Za-z-+\/]+);base64,(.+)$/);
    if (matches && matches.length === 3) {
      const mimeType = matches[1];
      const fileData = matches[2];

      const uploadData = {
        session: sessionId,
        note: {
          id: noteId,
          filename: `profile_photo_${Date.now()}.jpg`,
          file: fileData,
          file_mime_type: mimeType,
          related_module_name: "Users",
          related_module_id: user_id,
        },
      };

      await callSuiteCrmApi("set_note_attachment", uploadData);
      dispatch(setUserPhoto(formData.photo));
    }
  }

  // Sync all related data
  if (sessionId && user_id) {
    // Sync education details
    const educationPayload = buildEducationEntriesPayload(formData, user_id);
    await syncEducationEntries(sessionId, user_id, educationPayload, removalList);

    // Sync work experience details
    const workRemovalList = Array.isArray(formData.removedWorkIds)
      ? formData.removedWorkIds.filter(Boolean)
      : [];
    const workPayload = buildWorkEntriesPayload(formData, user_id);
    await syncWorkEntries(sessionId, user_id, workPayload, workRemovalList);

    // Sync ID card details
    const idCardRemovalList = Array.isArray(formData.removedIDCardIds)
      ? formData.removedIDCardIds.filter(Boolean)
      : [];
    const idCardPayload = buildIDCardEntriesPayload(formData, user_id);
    await syncIDCardEntries(sessionId, user_id, idCardPayload, idCardRemovalList);

    // Sync bank details
    const bankRemovalList = Array.isArray(formData.removedBankIds)
      ? formData.removedBankIds.filter(Boolean)
      : [];
    const bankPayload = buildBankEntriesPayload(formData, user_id);
    await syncBankDetailsEntries(sessionId, user_id, bankPayload, bankRemovalList);

    // Sync social links
    const socialLinksRemovalList = Array.isArray(formData.removedSocialLinksIds)
      ? formData.removedSocialLinksIds.filter(Boolean)
      : [];
    const socialLinksPayload = buildSocialLinksEntriesPayload(formData, user_id);
    await syncSocialLinksEntries(sessionId, user_id, socialLinksPayload, socialLinksRemovalList);
  }

  return response.data;
};

/**
 * Fetch updated profile data
 * @param {string} sessionId - Session ID
 * @param {string} user_id - User ID
 * @returns {Promise<Object>} Profile data
 */
export const fetchProfileData = async (sessionId, user_id) => {
  const apiUrl = "https://seltentechnologiesdemo.com/moona-backend/service/v4_1/rest.php";
  const method = "get_entry_list";
  const input_type = "JSON";
  const response_type = "JSON";
  const rest_data = JSON.stringify({
    session: sessionId,
    module_name: "Users",
    select_fields: [
      "id",
      "user_name",
      "first_name",
      "last_name",
      "email_c",
      "phone_mobile",
      "address_street",
      "address_c",
      "village_c",
      "taluk_c",
      "address_state",
      "address_country",
      "address_city",
      "address_postalcode",
      "referrer_c",
      "selfdescription_c",
      "dob_c",
      "aadhaar_c",
      "caste_c",
      "religion_c",
      "instagram_c",
      "facebook_c",
      "youtube_c",
      "linkedin_c",
      "threads_c",
      "address1_c",
      "address2_c",
      "district_c",
      "age_c",
      "alivestatus_c",
      "birthplace_c",
      "bloodgroup_c",
      "dateofdeath_c",
      "deathplace_c",
      "deathreason_c",
      "deathaddress_c",
      "deatharea_c",
      "deathpincode_c",
      "deathcity_c",
      "deathstate_c",
      "deathcountry_c",
      "disability_c",
      "disabilitytype_c",
      "gender_c",
      "languagesknown_c",
      "mothertongue_c",
      "maritalstatus_c",
      "marriagedate_c",
      "marriageplace_c",
      "economical_status_c",
      "education1_c",
      "education2_c",
      "education3_c",
      "education4_c",
      "education5_c",
      "educationtype1_c",
      "educationtype2_c",
      "educationtype3_c",
      "educationtype4_c",
      "educationtype5_c",
      "institute1_c",
      "institute2_c",
      "institute3_c",
      "institute4_c",
      "institute5_c",
      "yearofpassing1_c",
      "yearofpassing2_c",
      "yearofpassing3_c",
      "yearofpassing4_c",
      "yearofpassing5_c",
      "company_c",
      "employmenttype_c",
      "occupation_c",
      "worklocation_c",
    ],
  });

  const fullUrl = `${apiUrl}?method=${method}&input_type=${input_type}&response_type=${response_type}&rest_data=${encodeURIComponent(
    rest_data
  )}`;

  const response = await axios.get(fullUrl);
  const entryList = response.data.entry_list;
  const products = entryList.map((entry) => entry.name_value_list);
  const filteredProfiles = products.filter((item) => item.id.value === user_id);
  const profileData = filteredProfiles.length > 0 ? filteredProfiles[0] : null;

  return profileData;
};

/**
 * Fetch all related profile data (education, work, ID cards, bank details, social links)
 * @param {string} sessionId - Session ID
 * @param {string} user_id - User ID
 * @returns {Promise<Object>} Object containing all related data
 */
export const fetchAllRelatedData = async (sessionId, user_id) => {
  const [
    educationEntries,
    workEntries,
    idCardEntries,
    bankEntries,
    socialLinksEntries
  ] = await Promise.all([
    fetchEducationDetailsByParent(sessionId, user_id),
    fetchWorkDetailsByParent(sessionId, user_id),
    fetchIDCardDetailsByParent(sessionId, user_id),
    fetchBankDetailsByParent(sessionId, user_id),
    fetchSocialLinksByParent(sessionId, user_id),
  ]);

  return {
    educationEntries,
    workEntries,
    idCardEntries,
    bankEntries,
    socialLinksEntries,
  };
};
